<template>
    <div class="loja-perfeita-conditions-editor">
        <div class="d-flex justify-content-between align-items-center mb-4">
            <p class="text-muted mb-0">
                {{ hint }}
            </p>
            <b-button
                size="sm"
                class="btn-pdv-gradient-blue"
                :disabled="saving || loading"
                @click="openForm()"
            >
                <i class="fe-plus-circle" aria-hidden="true"></i>
                {{ getI18n(i18nKey, 'BUTTONS.adicionar_regra_segmentacao') }}
            </b-button>
        </div>

        <loja-perfeita-list-skeleton
            v-if="loading"
            :rows="3"
            :columns="7"
        />

        <template v-else>
            <b-table
                v-if="conditions.length > 0"
                :items="conditions"
                :fields="tableFields"
                striped
                responsive
                small
                class="mb-3"
            >
                <template v-slot:cell(channel_id)="{ item }">
                    {{ dimensionLabel(channelOptions, item.channel_id) }}
                </template>
                <template v-slot:cell(regional_id)="{ item }">
                    {{ dimensionLabel(regionalOptions, item.regional_id) }}
                </template>
                <template v-slot:cell(flag_id)="{ item }">
                    {{ dimensionLabel(flagOptions, item.flag_id) }}
                </template>
                <template v-slot:cell(pos_category_id)="{ item }">
                    {{ dimensionLabel(categoryOptions, item.pos_category_id) }}
                </template>
                <template v-slot:cell(chain_id)="{ item }">
                    {{ dimensionLabel(chainOptions, item.chain_id) }}
                </template>
                <template v-slot:cell(business_unit_id)="{ item }">
                    {{ dimensionLabel(businessUnitOptions, item.business_unit_id) }}
                </template>
                <template v-slot:cell(mix_id)="{ item }">
                    {{ dimensionLabel(mixOptions, item.mix_id) }}
                </template>
                <template v-slot:cell(actions)="{ item }">
                    <div class="table-button-container text-right">
                        <b-button
                            size="sm"
                            variant="outline-primary"
                            class="mr-1"
                            :disabled="saving"
                            @click="editCondition(item)"
                        >
                            <i class="fe-edit" aria-hidden="true"></i>
                        </b-button>
                        <b-button
                            size="sm"
                            variant="outline-danger"
                            :disabled="saving"
                            @click="confirmDelete(item)"
                        >
                            <i class="fe-trash-2" aria-hidden="true"></i>
                        </b-button>
                    </div>
                </template>
            </b-table>
            <b-alert
                v-else
                variant="info"
                show
                class="mb-3"
            >
                {{ getI18n(i18nKey, 'MESSAGES.empty_segmentacao') }}
            </b-alert>
        </template>

        <b-collapse :visible="formVisible" class="mt-2">
            <b-card class="border mb-2">
                <b-row>
                    <b-col md="4">
                        <b-form-group
                            :label="getI18n(i18nRelations, 'TYPES.channel')"
                            class="label-pdv mb-2"
                        >
                            <b-form-select
                                v-model="form.channel_id"
                                class="input-pdv-blue"
                                :options="channelOptions"
                                :disabled="saving || dimensionsLoading"
                            >
                                <template v-slot:first>
                                    <b-form-select-option :value="null">—</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                    <b-col md="4">
                        <b-form-group
                            :label="getI18n(i18nRelations, 'TYPES.regional')"
                            class="label-pdv mb-2"
                        >
                            <b-form-select
                                v-model="form.regional_id"
                                class="input-pdv-blue"
                                :options="regionalOptions"
                                :disabled="saving || dimensionsLoading"
                            >
                                <template v-slot:first>
                                    <b-form-select-option :value="null">—</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                    <b-col md="4">
                        <b-form-group
                            :label="getI18n(i18nRelations, 'TYPES.flag')"
                            class="label-pdv mb-2"
                        >
                            <b-form-select
                                v-model="form.flag_id"
                                class="input-pdv-blue"
                                :options="flagOptions"
                                :disabled="saving || dimensionsLoading"
                            >
                                <template v-slot:first>
                                    <b-form-select-option :value="null">—</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="4">
                        <b-form-group
                            :label="getI18n(i18nKey, 'FIELDS.seg_pos_category')"
                            class="label-pdv mb-2"
                        >
                            <b-form-select
                                v-model="form.pos_category_id"
                                class="input-pdv-blue"
                                :options="categoryOptions"
                                :disabled="saving || dimensionsLoading"
                            >
                                <template v-slot:first>
                                    <b-form-select-option :value="null">—</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                    <b-col md="4">
                        <b-form-group
                            :label="getI18n(i18nRelations, 'TYPES.chain')"
                            class="label-pdv mb-2"
                        >
                            <b-form-select
                                v-model="form.chain_id"
                                class="input-pdv-blue"
                                :options="chainOptions"
                                :disabled="saving || dimensionsLoading"
                            >
                                <template v-slot:first>
                                    <b-form-select-option :value="null">—</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="4">
                        <b-form-group
                            :label="getI18n(i18nRelations, 'TYPES.businessUnit')"
                            class="label-pdv mb-2"
                        >
                            <b-form-select
                                v-model="form.business_unit_id"
                                class="input-pdv-blue"
                                :options="businessUnitOptions"
                                :disabled="saving || dimensionsLoading"
                            >
                                <template v-slot:first>
                                    <b-form-select-option :value="null">—</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                    <b-col md="4">
                        <b-form-group
                            :label="getI18n(i18nKey, 'FIELDS.seg_mix')"
                            class="label-pdv mb-2"
                        >
                            <b-form-select
                                v-model="form.mix_id"
                                class="input-pdv-blue"
                                :options="mixOptions"
                                :disabled="saving || dimensionsLoading"
                            >
                                <template v-slot:first>
                                    <b-form-select-option :value="null">—</b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                </b-row>
                <div class="d-flex justify-content-end gap-2">
                    <b-button
                        size="sm"
                        class="btn btn-pdv-gradient-blue-transparent"
                        :disabled="saving"
                        @click="cancelForm"
                    >
                        {{ getI18n(i18nKey, 'BUTTONS.cancelar') }}
                    </b-button>
                    <b-button
                        size="sm"
                        class="btn btn-pdv-gradient-blue ml-2"
                        :disabled="saving || !formHasAnyDimension"
                        @click="saveCondition"
                    >
                        <b-spinner v-if="saving" small class="mr-1" />
                        {{ getI18n(i18nKey, 'BUTTONS.salvar') }}
                    </b-button>
                </div>
            </b-card>
        </b-collapse>
    </div>
</template>

<script>
import LojaPerfeitaService from '@src/services/dashboards/LojaPerfeitaService'
import PosChannelsService from '@src/services/PosChannelsService'
import PosRegionalsService from '@src/services/PosRegionalsService'
import PosFlagsService from '@src/services/PosFlagsService'
import PosCategoriesService from '@src/services/PosCategoriesService'
import PosChainsService from '@src/services/PosChainsService'
import BusinessUnitsService from '@src/services/BusinessUnitsService'
import MixesService from '@src/services/MixesService'
import LojaPerfeitaListSkeleton from './LojaPerfeitaListSkeleton.vue'
import Shared from '@src/mixins/shared.vue'
import swalFeedback from '@src/mixins/swalFeedback'

const i18nKey = 'LOJA_PERFEITA'
const i18nRelations = 'RELATIONS'

const emptyForm = () => ({
    id: null,
    channel_id: null,
    regional_id: null,
    flag_id: null,
    pos_category_id: null,
    chain_id: null,
    business_unit_id: null,
    mix_id: null,
})

function toSelectOptions(list) {
    return (list || []).map((item) => ({
        value: item.id,
        text: item.name || item.label || `#${item.id}`,
    }))
}

export default {
    components: { LojaPerfeitaListSkeleton },
    mixins: [Shared, swalFeedback],
    props: {
        parentType: {
            type: String,
            required: true,
            validator: (v) => ['campaign', 'pillar', 'criterion'].includes(v),
        },
        parentId: {
            type: [Number, String],
            required: true,
        },
        hint: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            i18nKey,
            i18nRelations,
            loading: false,
            saving: false,
            dimensionsLoading: false,
            conditions: [],
            formVisible: false,
            form: emptyForm(),
            channelOptions: [],
            regionalOptions: [],
            flagOptions: [],
            categoryOptions: [],
            chainOptions: [],
            businessUnitOptions: [],
            mixOptions: [],
            dimensionsLoaded: false,
        }
    },
    computed: {
        tableFields() {
            return [
                { key: 'channel_id', label: this.getI18n(i18nRelations, 'TYPES.channel') },
                { key: 'regional_id', label: this.getI18n(i18nRelations, 'TYPES.regional') },
                { key: 'flag_id', label: this.getI18n(i18nRelations, 'TYPES.flag') },
                { key: 'pos_category_id', label: this.getI18n(i18nKey, 'FIELDS.seg_pos_category') },
                { key: 'chain_id', label: this.getI18n(i18nRelations, 'TYPES.chain') },
                { key: 'business_unit_id', label: this.getI18n(i18nRelations, 'TYPES.businessUnit') },
                { key: 'mix_id', label: this.getI18n(i18nKey, 'FIELDS.seg_mix') },
                { key: 'actions', label: this.getI18n(i18nKey, 'TITLES.acoes') },
            ]
        },
        formHasAnyDimension() {
            return (
                this.form.channel_id != null ||
                this.form.regional_id != null ||
                this.form.flag_id != null ||
                this.form.pos_category_id != null ||
                this.form.chain_id != null ||
                this.form.business_unit_id != null ||
                this.form.mix_id != null
            )
        },
    },
    watch: {
        parentId() {
            this.loadConditions()
        },
    },
    methods: {
        loadConditions() {
            if (!this.parentId) return
            this.loading = true
            let promise
            switch (this.parentType) {
                case 'campaign':
                    promise = LojaPerfeitaService.getCampaignConditions(this.parentId)
                    break
                case 'pillar':
                    promise = LojaPerfeitaService.getPillarConditions(this.parentId)
                    break
                case 'criterion':
                    promise = LojaPerfeitaService.getCriterionConditions(this.parentId)
                    break
            }
            promise
                .then((res) => {
                    this.conditions = res.data || []
                })
                .catch(() => {
                    this.conditions = []
                })
                .finally(() => {
                    this.loading = false
                })
        },
        loadDimensions() {
            if (this.dimensionsLoaded) return Promise.resolve()
            this.dimensionsLoading = true
            const fetchAll = { page: 1, per_page: 999 }
            return Promise.all([
                PosChannelsService.fetchAll(fetchAll).catch(() => ({ data: { data: [] } })),
                PosRegionalsService.fetchAll(fetchAll).catch(() => ({ data: { data: [] } })),
                PosFlagsService.fetchAll(fetchAll).catch(() => ({ data: { data: [] } })),
                PosCategoriesService.fetchAll(fetchAll).catch(() => ({ data: { data: [] } })),
                PosChainsService.fetchAll(fetchAll).catch(() => ({ data: { data: [] } })),
                BusinessUnitsService.fetchAll(fetchAll).catch(() => ({ data: { data: [] } })),
                MixesService.fetchAll(fetchAll).catch(() => ({ data: { data: [] } })),
            ]).then(([channels, regionals, flags, categories, chains, businessUnits, mixes]) => {
                const extractList = (res) => {
                    const inner = res.data
                    return Array.isArray(inner) ? inner : (inner && inner.data) || []
                }
                this.channelOptions = toSelectOptions(extractList(channels))
                this.regionalOptions = toSelectOptions(extractList(regionals))
                this.flagOptions = toSelectOptions(extractList(flags))
                this.categoryOptions = toSelectOptions(extractList(categories))
                this.chainOptions = toSelectOptions(extractList(chains))
                this.businessUnitOptions = toSelectOptions(extractList(businessUnits))
                this.mixOptions = toSelectOptions(extractList(mixes))
                this.dimensionsLoaded = true
            }).finally(() => {
                this.dimensionsLoading = false
            })
        },
        openForm(condition = null) {
            this.loadDimensions()
            if (condition) {
                this.form = {
                    id: condition.id,
                    channel_id: condition.channel_id || null,
                    regional_id: condition.regional_id || null,
                    flag_id: condition.flag_id || null,
                    pos_category_id: condition.pos_category_id || null,
                    chain_id: condition.chain_id || null,
                    business_unit_id: condition.business_unit_id || null,
                    mix_id: condition.mix_id || null,
                }
            } else {
                this.form = emptyForm()
            }
            this.formVisible = true
        },
        cancelForm() {
            this.formVisible = false
            this.form = emptyForm()
        },
        editCondition(item) {
            this.openForm(item)
        },
        saveCondition() {
            if (this.saving || !this.formHasAnyDimension) return
            this.saving = true
            const payload = {
                channel_id: this.form.channel_id || null,
                regional_id: this.form.regional_id || null,
                flag_id: this.form.flag_id || null,
                pos_category_id: this.form.pos_category_id || null,
                chain_id: this.form.chain_id || null,
                business_unit_id: this.form.business_unit_id || null,
                mix_id: this.form.mix_id || null,
            }

            let promise
            if (this.form.id) {
                promise = LojaPerfeitaService.updateCondition(this.form.id, payload)
            } else {
                switch (this.parentType) {
                    case 'campaign':
                        promise = LojaPerfeitaService.createCampaignCondition(this.parentId, payload)
                        break
                    case 'pillar':
                        promise = LojaPerfeitaService.createPillarCondition(this.parentId, payload)
                        break
                    case 'criterion':
                        promise = LojaPerfeitaService.createCriterionCondition(this.parentId, payload)
                        break
                }
            }

            promise
                .then(() => {
                    this.successFeedback(
                        this.getI18n(i18nKey, 'TITLES.segmentacao'),
                        this.getI18n(i18nKey, 'MESSAGES.segmentacao_salva')
                    )
                    this.cancelForm()
                    this.loadConditions()
                })
                .catch((err) => {
                    this.negativeFeedback(
                        this.getI18n('RESPONSES', 'something_wrong'),
                        err?.response?.data?.message || err?.message || ''
                    )
                })
                .finally(() => {
                    this.saving = false
                })
        },
        confirmDelete(item) {
            this.$swal({
                title: this.getI18n(i18nKey, 'MESSAGES.confirm_excluir_segmentacao_title'),
                text: this.getI18n(i18nKey, 'MESSAGES.confirm_excluir_segmentacao'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: this.getI18n(i18nKey, 'BUTTONS.excluir'),
                cancelButtonText: this.getI18n(i18nKey, 'BUTTONS.cancelar'),
            }).then((result) => {
                if (result.isConfirmed) {
                    this.deleteCondition(item)
                }
            })
        },
        deleteCondition(item) {
            this.saving = true
            LojaPerfeitaService.deleteCondition(item.id)
                .then(() => {
                    this.successFeedback(
                        this.getI18n(i18nKey, 'TITLES.segmentacao'),
                        this.getI18n(i18nKey, 'MESSAGES.segmentacao_excluida')
                    )
                    this.loadConditions()
                })
                .catch((err) => {
                    this.negativeFeedback(
                        this.getI18n('RESPONSES', 'something_wrong'),
                        err?.response?.data?.message || err?.message || ''
                    )
                })
                .finally(() => {
                    this.saving = false
                })
        },
        dimensionLabel(options, id) {
            if (id == null) return '—'
            const opt = options.find((o) => o.value === id || o.value === Number(id))
            return opt ? opt.text : `#${id}`
        },
    },
}
</script>
